import React from 'react';
import { Bus, Calendar, Briefcase, MapPin, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services } from '../data/mock';

export const Services = () => {
  const iconMap = {
    Bus: Bus,
    Calendar: Calendar,
    Briefcase: Briefcase,
    MapPin: MapPin
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#003d82] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Os Nossos Serviços</h1>
            <p className="text-xl text-gray-200">
              Soluções completas de transporte rodoviário para empresas, escolas, grupos e particulares.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {services.filter(s => s.id === 'cim-cavado' || s.id === 'regular' || s.id === 'specialized').map((service, index) => {
              const Icon = iconMap[service.icon];
              // CIM Cávado não deve ter "Pedir orçamento"
              const showQuoteButton = service.id !== 'cim-cavado';
              
              return (
                <div
                  key={service.id}
                  className="group bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-[#f5a623] hover:shadow-2xl transition-all duration-300"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 rounded-xl bg-[#f5a623]/10 flex items-center justify-center mb-6 group-hover:bg-[#f5a623] transition-colors">
                      <Icon size={40} className="text-[#003d82] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-[#003d82] mb-3">{service.title}</h3>
                      <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                      
                      <div className="mb-6">
                        <h4 className="font-semibold text-[#003d82] mb-3">Características:</h4>
                        <ul className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-3 text-gray-700 justify-center">
                              <CheckCircle size={18} className="text-[#f5a623] flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {showQuoteButton && (
                        <Link
                          to="/contacto"
                          className="inline-flex items-center gap-2 text-[#003d82] font-semibold hover:text-[#f5a623] transition-colors"
                        >
                          Pedir orçamento
                          <ArrowRight size={18} />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* UNIR Details */}
          <div className="max-w-5xl mx-auto mb-16">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="p-8 md:p-12">
                  <div className="w-16 h-16 rounded-lg bg-[#f5a623]/10 flex items-center justify-center mb-6">
                    <Bus size={32} className="text-[#003d82]" />
                  </div>
                  <h3 className="text-3xl font-bold text-[#003d82] mb-4">Carreiras UNIR</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Operamos 7 linhas da rede UNIR (Lote 3), garantindo o transporte diário de milhares de 
                    passageiros entre a Póvoa de Varzim, Vila do Conde e localidades vizinhas.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-[#f5a623]" />
                      <span className="text-gray-700">Linhas: 3001, 3002, 3003, 3004, 3006, 3301, 3302</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-[#f5a623]" />
                      <span className="text-gray-700">Horários regulares de segunda a sexta</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-[#f5a623]" />
                      <span className="text-gray-700">Ligações ao Metro do Porto</span>
                    </div>
                  </div>
                  <Link
                    to="/horarios"
                    className="inline-flex items-center gap-2 bg-[#003d82] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#002a5c] transition-all"
                  >
                    Ver Horários
                    <ArrowRight size={18} />
                  </Link>
                </div>
                <div className="h-[300px] md:h-auto">
                  <img
                    src="https://customer-assets.emergentagent.com/job_povoa-transport/artifacts/2135g1ad_WhatsApp%20Image%202026-04-08%20at%2014.12.24.jpeg"
                    alt="Autocarro UNIR"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tourism Details */}
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="h-[300px] md:h-auto order-2 md:order-1">
                  <img
                    src="https://images.unsplash.com/photo-1616607006500-b08d26749c64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxQb3J0dWdhbCUyMHRvdXJpc218ZW58MHx8fHwxNzc1NjU0NDM2fDA&ixlib=rb-4.1.0&q=85"
                    alt="Turismo em Portugal"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 md:p-12 order-1 md:order-2">
                  <div className="w-16 h-16 rounded-lg bg-[#f5a623]/10 flex items-center justify-center mb-6">
                    <MapPin size={32} className="text-[#003d82]" />
                  </div>
                  <h3 className="text-3xl font-bold text-[#003d82] mb-4">Turismo</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Descubra Portugal, Espanha, França e outros destinos europeus com o conforto e segurança 
                    dos nossos serviços de turismo. Autocarros modernos e motoristas experientes para tornar 
                    a sua viagem inesquecível, seja uma excursão nacional ou uma aventura internacional.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-[#f5a623]" />
                      <span className="text-gray-700">Viagens personalizadas</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-[#f5a623]" />
                      <span className="text-gray-700">Destinos nacionais e internacionais</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-[#f5a623]" />
                      <span className="text-gray-700">Experiência em viagens pela Europa</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-[#f5a623]" />
                      <span className="text-gray-700">Autocarros confortáveis e equipados</span>
                    </div>
                  </div>
                  <Link
                    to="/contacto"
                    className="inline-flex items-center gap-2 bg-[#f5a623] text-[#003d82] px-6 py-3 rounded-lg font-semibold hover:bg-[#e5a71b] transition-all"
                  >
                    Pedir Orçamento
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#003d82] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Precisa de um Serviço Personalizado?</h2>
            <p className="text-xl mb-8 text-gray-200">
              Entre em contacto e a nossa equipa elaborará uma proposta à medida das suas necessidades.
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 bg-[#f5a623] text-[#003d82] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#e5a71b] transition-all duration-300 hover:shadow-xl hover:scale-105"
            >
              Contactar Agora
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
